import { Box, Typography } from "@mui/material";
import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  TYPOGRAPHY_SCALE,
  Z_INDEX,
} from "src/domains/shared/constants/styles";
import {
  exitReviewMode,
  isReviewMode,
} from "src/domains/shared/utils/reviewMode";

/** 모드는 한 페이지 수명 동안 바뀌지 않는다(`reviewMode`) — 구독할 변화가 없다. */
const subscribeToNothing = () => () => {};
/** 서버와 첫 클라이언트 렌더는 그리지 않는다 — 정적 HTML 에는 탭의 모드가 없다. */
const notOnServer = () => false;

/**
 * 검수 모드라는 표시와 끝내기 — 앱 뿌리(`_app`)에 한 번 서서 모든 페이지에 보인다(`review-mode`).
 *
 * 문면은 보는 사람이 누구든 참이다. 계정의 등급을 읽지 않는다 — 화면은 누가 관리자인지 모르고, 탭이 검수 모드인지만 안다.
 */
export function ReviewModeIndicator() {
  const { t } = useTranslation("common");
  const isReviewing = useSyncExternalStore(
    subscribeToNothing,
    isReviewMode,
    notOnServer
  );

  if (!isReviewing) return null;

  return (
    <Box role="status" sx={slot}>
      <Typography component="span" sx={label}>
        {t("reviewMode.label")}
      </Typography>
      <Button
        size="small"
        emphasis="outlined"
        intent="inherit"
        onClick={exitReviewMode}
      >
        {t("reviewMode.exit")}
      </Button>
    </Box>
  );
}

/**
 * 화면 위쪽 가운데의 작은 한 줄. 어느 페이지에서도 조작부를 덮지 않도록 프로젝트 목록의 머리 줄 버튼(아래 끝 86px)과
 * 검색 줄(위 끝 121px) 사이의 띠에 들어가는 높이로 둔다 — 뷰어의 상단 컨트롤(날짜 선택·분할 선택)보다는 아래다.
 * 지도·3D 캔버스 위에 뜨므로 표시는 포인터를 받지 않고 끝내기 버튼만 받는다 — 표시 아래의 지도를 그대로 끌 수 있다.
 */
const slot = {
  position: "fixed",
  top: `${SPACING_CONSTANTS.spacing[20] + SPACING_CONSTANTS.spacing[2]}px`,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: Z_INDEX.banner,
  display: "flex",
  alignItems: "center",
  gap: `${SPACING_CONSTANTS.spacing[2]}px`,
  height: `${SPACING_CONSTANTS.spacing[8]}px`,
  paddingLeft: `${SPACING_CONSTANTS.spacing[3]}px`,
  paddingRight: `${SPACING_CONSTANTS.spacing[1] / 2}px`,
  borderRadius: SPACING_CONSTANTS.borderRadius.lg,
  backgroundColor: COLOR_CONSTANTS.alert.info.background,
  boxShadow: SPACING_CONSTANTS.shadows.sm,
  whiteSpace: "nowrap",
  pointerEvents: "none",
  "& button": { pointerEvents: "auto" },
} as const;

const label = {
  ...TYPOGRAPHY_SCALE.alertDescription,
  color: COLOR_CONSTANTS.alert.info.color,
} as const;
